import {md5hash} from './picos/hash';
import type {uuid} from '../@types/schema';

/**
 * Convert a Date object to a stable cache key string.
 * Returns the timestamp as a string, or 'null' for null/undefined dates.
 */
export function dateKey(date: Date | null | undefined): string {
    return date ? date.getTime().toString() : 'null';
}

/**
 * Create a sorted MD5 hash from an array of IDs.
 * Sorting ensures consistent hashes regardless of input order.
 */
export function hashIds(ids: (string | uuid)[]): string {
    if (!ids || ids.length === 0) return 'empty';
    return md5hash(ids.filter(Boolean).sort().join(','));
}

/**
 * Create a hash from multiple ID arrays combined.
 */
export function hashMultipleIdArrays(...idArrays: (string | uuid)[][]): string {
    const allIds: string[] = [];
    for (const arr of idArrays) {
        if (arr) {
            for (const id of arr) {
                if (id) allIds.push(id);
            }
        }
    }
    return hashIds(allIds);
}

/**
 * Generic cache entry type for hash-based invalidation.
 */
type CacheEntry<TResult> = {
    result: TResult;
    depsHash: string;
};

/**
 * Create a hash-based cache for functions with a single key.
 * The cache invalidates when the dependencies hash changes, not based on time.
 *
 * @param keyFn - Function to extract a cache key from the input
 * @param hashFn - Function to compute dependency hash from the input
 * @param computeFn - The actual computation to cache
 * @returns A cached version of computeFn
 */
export function createHashCache<TKey, TResult>(
    keyFn: (key: TKey) => string,
    hashFn: (key: TKey) => string,
    computeFn: (key: TKey) => TResult
): (key: TKey) => TResult {
    const cache = new Map<string, CacheEntry<TResult>>();

    return (key: TKey): TResult => {
        const cacheKey = keyFn(key);
        const hash = hashFn(key);
        const cached = cache.get(cacheKey);

        if (cached && cached.depsHash === hash) {
            return cached.result;
        }

        const result = computeFn(key);
        cache.set(cacheKey, {result, depsHash: hash});
        return result;
    };
}

/**
 * Create a hash-based cache for functions with two keys (e.g., date ranges).
 *
 * @param keyFn - Function to extract a cache key from inputs
 * @param hashFn - Function to compute dependency hash from inputs
 * @param computeFn - The actual computation to cache
 * @returns A cached version of computeFn
 */
export function createHashCache2<TKey1, TKey2, TResult>(
    keyFn: (key1: TKey1, key2: TKey2) => string,
    hashFn: (key1: TKey1, key2: TKey2) => string,
    computeFn: (key1: TKey1, key2: TKey2) => TResult
): (key1: TKey1, key2: TKey2) => TResult {
    const cache = new Map<string, CacheEntry<TResult>>();

    return (key1: TKey1, key2: TKey2): TResult => {
        const cacheKey = keyFn(key1, key2);
        const hash = hashFn(key1, key2);
        const cached = cache.get(cacheKey);

        if (cached && cached.depsHash === hash) {
            return cached.result;
        }

        const result = computeFn(key1, key2);
        cache.set(cacheKey, {result, depsHash: hash});
        return result;
    };
}

/**
 * Create a hash-based cache for functions with three keys.
 */
export function createHashCache3<TKey1, TKey2, TKey3, TResult>(
    keyFn: (key1: TKey1, key2: TKey2, key3: TKey3) => string,
    hashFn: (key1: TKey1, key2: TKey2, key3: TKey3) => string,
    computeFn: (key1: TKey1, key2: TKey2, key3: TKey3) => TResult
): (key1: TKey1, key2: TKey2, key3: TKey3) => TResult {
    const cache = new Map<string, CacheEntry<TResult>>();

    return (key1: TKey1, key2: TKey2, key3: TKey3): TResult => {
        const cacheKey = keyFn(key1, key2, key3);
        const hash = hashFn(key1, key2, key3);
        const cached = cache.get(cacheKey);

        if (cached && cached.depsHash === hash) {
            return cached.result;
        }

        const result = computeFn(key1, key2, key3);
        cache.set(cacheKey, {result, depsHash: hash});
        return result;
    };
}

/**
 * Simple memoization for functions with object arguments (compares by ID).
 * Good for functions that take an entity and optional parameters.
 */
export function memoizeByEntityId<TEntity extends { id: uuid }, TResult>(
    computeFn: (entity: TEntity) => TResult
): (entity: TEntity) => TResult {
    const cache = new Map<uuid, { result: TResult; entityAddedOn: number }>();

    return (entity: TEntity): TResult => {
        if (!entity) return computeFn(entity);

        const cached = cache.get(entity.id);
        const entityAddedOn = (entity as any).addedOn as number;

        // Cache hit if entity hasn't been updated
        if (cached && cached.entityAddedOn === entityAddedOn) {
            return cached.result;
        }

        const result = computeFn(entity);
        cache.set(entity.id, {result, entityAddedOn});
        return result;
    };
}

/**
 * Create a cache that clears its entries based on a clear condition.
 * This is useful for caches that depend on external state.
 */
export function createClearableCache<TKey, TResult>(
    keyFn: (key: TKey) => string,
    computeFn: (key: TKey) => TResult
): {
    get: (key: TKey) => TResult;
    clear: () => void;
    clearKey: (key: TKey) => void;
} {
    const cache = new Map<string, TResult>();

    return {
        get: (key: TKey): TResult => {
            const cacheKey = keyFn(key);
            const cached = cache.get(cacheKey);

            if (cached !== undefined) {
                return cached;
            }

            const result = computeFn(key);
            cache.set(cacheKey, result);
            return result;
        },
        clear: () => cache.clear(),
        clearKey: (key: TKey) => cache.delete(keyFn(key))
    };
}
